import {
	Heading,
	Text,
	Box,
	Button,
	Table,
	AsyncSelect,
	Time,
	Inline,
	Icons,
	Tabs,
	TabsList,
	TabContent,
	Stack,
	joinAttributes,
	RelativeTime,
	Dropdown,
	Component,
	Divider,
	Badge,
	Spinner,
	useBreakpoints,
	Select,
	CheckboxInput,
	useCallbackRef,
	Grid,
	Col,
	TabItem,
} from "@sembark-travel/ui-base"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import {
	ListView,
	areAdvancedFiltersAppliedDefault,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { Omit, Optional } from "utility-types"
import React, {
	Fragment,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react"
import { ComposeEmail } from "../MailMessages"
import { WhatsAppLinkedConversations } from "../WhatsAppMessages"
import { SelectUsers } from "../Users"
import { ITripPlanRequest, LeadStatusType, LeadXHR } from "./store"
import useSWR from "swr"
import { PhoneNumber, Email } from "../Contacts"
import { useCheckPermissions, PERMISSIONS, IUser, useAuthUser } from "../Auth"
import {
	Form,
	withServerErrors,
	validateFormValues,
	SubmissionError,
	SelectField,
	parsePhoneNumber,
	DatePickerField,
	SwitchInputField,
	useFieldValue,
	TextAreaInputField,
	TextInputField,
	GetFieldValue,
} from "@sembark-travel/ui-form"
import arrayMutators from "final-form-arrays"
import * as Validator from "yup"
import { AddNewLeadInDialog, LeadDetailsForm } from "./NewItem"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	parseDate,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import { generatePath, useNavigate } from "../router-utils"
import { ITag, SelectTags, TagBadges } from "../Tags"
import { TemporaryStorage } from "../DownloadManager"
import config from "../config"
import {
	CommentableCommentsList,
	TWhatsAppReminderValues,
	WhatsAppReminderFields,
	whatsAppReminderPayload,
} from "../Comments"
import { checkFilterAppliedFn } from "../utils"
import { transformAddressToEditableValue } from "../Addresses"
import { PastInteractionsInDialog } from "./PastInteractions"
import { TLeadChannel } from "../LeadChannels"
import { SelectHotels } from "../Hotels"
import { ActivityLogs } from "../ActivityLogs"
import { SelectLeadChannels } from "../LeadChannels"
import { SelectTripSources } from "../TripSources"

type TFilters = TSearchParams & {
	source?: string
	tags?: Array<ITag>
	owners?: Array<IUser>
	created_after?: Date
	created_before?: Date
	unassigned_only?: boolean
	ai_only?: boolean
	destinations?: Array<{ id: string; name: string }>
	leadStatus?: Array<{ name: string; id: string }>
	trip_sources?: Array<{ name: string; id: number }>
	lead_channels?: Array<{ name: string; id: number }>
	lead_purposes?: Array<{ name: string; id: number }>
	lead_close_reasons?: Array<{ name: string; id: number }>
	hotels_of_interest?: Array<{ id: number; name: string }>
}

type TFiltersInQuery = TSearchParams & {
	source: "all" | "archived_only" | "new_only" | string
	tags?: Array<string>
	owners?: Array<string>
	ca?: string
	cb?: string
	unassigned_only?: 1
	ai_only?: 1
	destinations?: Array<string>
	leadStatus?: Array<string>
	trip_sources?: Array<string>
	lead_channels?: Array<string>
	lead_purposes?: Array<string>
	lead_close_reasons?: Array<string>
	hotels_of_interest?: Array<string>
}

interface IEditTagsAndFollowUpsFormValues extends TWhatsAppReminderValues {
	tags?: Array<ITag>
	due_at?: Date
	note?: string
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const {
		source,
		tags,
		q,
		page,
		created_after,
		created_before,
		unassigned_only,
		ai_only,
		owners,
		destinations,
		leadStatus,
		trip_sources,
		lead_channels,
		lead_purposes,
		lead_close_reasons,
		hotels_of_interest,
	} = filters
	const query: TFiltersInQuery = { source: source || "new_only" }
	if (tags?.length) {
		query.tags = tags.map((t) => `${t.id}-${t.name}`)
	}
	if (owners?.length) {
		query.owners = owners.map((t) => `${t.id}-${t.name}`)
	}
	if (destinations?.length) {
		query.destinations = destinations.map((t) => `${t.id}-${t.name}`)
	}
	if (leadStatus?.length) {
		query.leadStatus = leadStatus.map((t) => `${t.id}-${t.name}`)
	}
	if (trip_sources?.length) {
		query.trip_sources = trip_sources.map((v) => `${v.id}_${v.name}`)
	}
	if (q?.length) {
		query.q = q
	}
	if (page) {
		query.page = page
	}
	if (created_after) {
		query.ca = dateToQuery(created_after)
	}
	if (created_before) {
		query.cb = dateToQuery(created_before)
	}
	if (unassigned_only) query.unassigned_only = 1
	if (ai_only) query.ai_only = 1
	if (lead_channels?.length) {
		query.lead_channels = lead_channels.map((v) => `${v.id}_${v.name}`)
	}
	if (lead_purposes?.length) {
		query.lead_purposes = lead_purposes.map((v) => `${v.id}_${v.name}`)
	}
	if (lead_close_reasons?.length) {
		query.lead_close_reasons = lead_close_reasons.map(
			(v) => `${v.id}_${v.name}`
		)
	}
	if (hotels_of_interest?.length) {
		query.hotels_of_interest = hotels_of_interest.map(
			(h) => `${h.id}_${h.name}`
		)
	}
	return query
}

function queryToFilter(query: TFiltersInQuery): TFilters {
	const {
		source,
		tags,
		q,
		page,
		ca,
		cb,
		unassigned_only,
		ai_only,
		owners,
		destinations,
		leadStatus,
		trip_sources,
		lead_channels,
		lead_purposes,
		lead_close_reasons,
		hotels_of_interest,
	} = query
	const filters: TFilters = { source: source || "new_only" }
	if (tags?.length) {
		filters.tags = tags.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as ITag
		})
	}
	if (owners?.length) {
		filters.owners = owners.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as IUser
		})
	}
	if (destinations?.length) {
		filters.destinations = destinations.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { id: string; name: string }
		})
	}
	if (leadStatus?.length) {
		filters.leadStatus = leadStatus.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { name: string; id: string }
		})
	}
	if (trip_sources?.length) {
		filters.trip_sources = trip_sources.map((v) => {
			const [id, ...name] = v.split("_")
			return {
				id: Number(id),
				name: name.join("_"),
			}
		})
	}

	if (lead_channels?.length) {
		filters.lead_channels = lead_channels.map((t) => {
			const [id, ...name] = t.split("_")
			return {
				id: Number(id),
				name: name.join("_"),
			}
		})
	}
	if (lead_purposes?.length) {
		filters.lead_purposes = lead_purposes.map((t) => {
			const [id, ...name] = t.split("_")
			return {
				id: Number(id),
				name: name.join("_"),
			}
		})
	}
	if (lead_close_reasons?.length) {
		filters.lead_close_reasons = lead_close_reasons.map((v) => {
			const [id, ...name] = v.split("_")
			return {
				id: Number(id),
				name: name.join("_"),
			}
		})
	}
	if (hotels_of_interest?.length) {
		filters.hotels_of_interest = hotels_of_interest.map((h) => {
			const [id, ...name] = h.split("_")
			return {
				id: Number(id),
				name: name.join("_"),
			}
		})
	}
	if (q?.length) {
		filters.q = q
	}
	if (page) {
		filters.page = page
	}
	if (ca) {
		filters.created_after = parseDateFromQuery(ca)
	}
	if (cb) {
		filters.created_before = parseDateFromQuery(cb)
	}
	if (unassigned_only) filters.unassigned_only = true
	if (ai_only) filters.ai_only = true
	return filters
}

export function LeadsList() {
	const { user } = useAuthUser()
	const [refreshKey, setRefreshKey] = useState(1)
	const { hasPermission } = useCheckPermissions()
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInQuery>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilter,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
		source: query.source || "new_only",
	})
	const canManageLead = hasPermission(PERMISSIONS.MANAGE_TRIP_PLAN_REQUESTS)
	const canCreateLead = hasPermission(PERMISSIONS.CREATE_QUERIES)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const { xs } = useBreakpoints()

	const requestPayload = useMemo(() => {
		const {
			source,
			tags,
			owners,
			leadStatus,
			created_after,
			created_before,
			destinations,
			trip_sources,
			lead_channels,
			lead_purposes,
			lead_close_reasons,
			hotels_of_interest,
			...other
		} = params || {}

		return {
			...other,
			trip_sources:
				source === "all" || source === "moved_to_trips_only"
					? trip_sources?.map((v) => v.id)
					: undefined,
			archived_only: source === "archived_only" ? 1 : null,
			new_only: source === "new_only" ? 1 : null,
			tags: tags?.map((t) => t.id),
			owners: owners?.map((t) => t.id),
			destinations: destinations?.map((t) => t.id),
			lead_channels: lead_channels?.map((t) => t.id),
			lead_purposes: lead_purposes?.map((t) => t.id),
			lead_close_reasons: lead_close_reasons?.map((l) => l.id),
			hotels_of_interest: hotels_of_interest?.map((h) => h.id),
			lead_status:
				source === "moved_to_trips_only"
					? "moved"
					: leadStatus?.map((t) => t.id).join(","),
			created_after: created_after
				? dateToUTCString(startOf(created_after, "day"))
				: null,
			created_before: created_before
				? dateToUTCString(endOf(created_before, "day"))
				: null,
		}
	}, [params])
	return (
		<Fragment>
			<Search
				initialParams={params}
				onSearch={(params) => {
					setParams({ ...params, page: 1 })
				}}
				title="Leads"
				resetParams={({ source }) => ({
					source,
					page: 1,
				})}
				areAdvancedFiltersApplied={(params) => {
					const { source, unassigned_only, ai_only, ...otherParams } = params
					return (
						areAdvancedFiltersAppliedDefault(otherParams) ||
						Boolean(unassigned_only) ||
						Boolean(ai_only)
					)
				}}
				actions={({ changeAdvancedFiltersVisibility }) => (
					<Inline justifyContent="between" gap="2">
						<Inline gap={"2"}>
							{canManageLead ? (
								<>
									<ButtonLink
										to={generatePath(
											"/trip-plan-requests/auto-distribution-preferences"
										)}
										color="accent"
									>
										<Icons.Cog color="default" />
									</ButtonLink>

									<ButtonLink
										to={generatePath("/trip-plan-requests/report")}
										color="primary"
										level="secondary"
									>
										Report
									</ButtonLink>
								</>
							) : null}

							{canCreateLead ? (
								<AddNewLeadInDialog
									onSuccess={() => {
										setParams({ ...params, page: 1 })
										setRefreshKey((prev) => prev + 1)
									}}
								>
									{({ create }) => (
										<Button level="primary" status="primary" onClick={create}>
											Add Lead
										</Button>
									)}
								</AddNewLeadInDialog>
							) : null}
						</Inline>
						{canManageLead ? (
							<Dropdown alignRight>
								<Dropdown.ToggleButton>
									<Icons.DotsVertical />
								</Dropdown.ToggleButton>
								<Dropdown.Menu>
									<TemporaryStorage
										initUrl="/trip-plan-requests"
										initParams={{
											...requestPayload,
											_download: 1,
											timezone_offset: config.timezoneOffset,
										}}
									>
										{({ message, generate, url, reset }) =>
											url ? (
												<Dropdown.MenuItem
													as="a"
													href={url}
													target="_blank"
													download
													onClick={() => reset()}
												>
													{message}
												</Dropdown.MenuItem>
											) : (
												<Dropdown.MenuItem
													onClick={(e: React.MouseEvent) => {
														e.preventDefault()
														generate()
													}}
												>
													{message || (
														<>
															<Icons.DocumentDownload /> Download as CSV/Excel
														</>
													)}
												</Dropdown.MenuItem>
											)
										}
									</TemporaryStorage>
									<BulkAssignLeads
										params={requestPayload}
										onSuccess={() => {
											setParams({ ...params })
											setRefreshKey((prev) => prev + 1)
										}}
										openAdvancedFilters={() => {
											changeAdvancedFiltersVisibility(true)
										}}
									>
										{({ onAssign }) => (
											<Dropdown.MenuItem onClick={onAssign}>
												<Icons.User /> Bulk Assign Leads{" "}
											</Dropdown.MenuItem>
										)}
									</BulkAssignLeads>
								</Dropdown.Menu>
							</Dropdown>
						) : null}
					</Inline>
				)}
				Filters={Filters}
			>
				{({ searchParams, setSearchParams }) => (
					<Tabs layout="col">
						<TabsList>
							<TabLink
								params={searchParams}
								setParams={setSearchParams}
								source="new_only"
								key="new_only"
							>
								New
							</TabLink>
							<TabLink
								params={searchParams}
								setParams={setSearchParams}
								source="moved_to_trips_only"
								key="moved_to_trips_only"
							>
								Moved to Trip
							</TabLink>
							<TabLink
								params={searchParams}
								setParams={setSearchParams}
								source="archived_only"
								key="archived_only"
							>
								Closed
							</TabLink>
							<TabLink
								params={searchParams}
								setParams={setSearchParams}
								source="all"
								key="all_all"
							>
								All
							</TabLink>
						</TabsList>
						<TabContent style={{ minHeight: "50vh" }}>
							<ListView<ITripPlanRequest, TFilters>
								params={params}
								pageKey={`trip-plan-requests_${refreshKey}`}
								swrConfig={{ revalidateOnFocus: true }}
								fetch={(xhr) => {
									return LeadXHR(xhr).getLeads(requestPayload)
								}}
								onPageChange={(page) => setParams({ ...params, page })}
							>
								{({ items, refresh }) =>
									xs ? (
										<Box>
											<Stack gap="2" as="ol">
												{items.map((r) => (
													<Box
														as="li"
														key={r.id}
														bgColor="default"
														padding="4"
														rounded="md"
														borderWidth="1"
													>
														<Box style={{ float: "right" }} paddingLeft="2">
															<Actions request={r} onChange={refresh} />
														</Box>
														<Stack gap="2">
															<GuestDetails
																request={r}
																showFullDetailsInDialog
															/>
															<Inline gap="1">
																<Box flex="1" minWidth="0">
																	{r.owner ? (
																		<Stack gap="1">
																			{r.owner.id !== user?.id ? (
																				<Text>{r.owner.name}</Text>
																			) : null}
																			{r.assigned_by ? (
																				<Stack>
																					<Text
																						color="muted"
																						fontWeight="semibold"
																					>
																						Assigned by
																					</Text>
																					<Text color="muted">
																						{r.assigned_by?.name}
																					</Text>
																				</Stack>
																			) : null}
																			{r.assigned_at ? (
																				<Box>
																					<RelativeTime
																						timestamp={r.assigned_at}
																					/>
																				</Box>
																			) : null}
																		</Stack>
																	) : (
																		<Box>-</Box>
																	)}
																</Box>
															</Inline>
															<Stack gap="2">
																{r.tags?.length || r.notes?.length ? (
																	<>
																		<TagBadges tags={r.tags} />
																		{/* Follow up notes and comments are same so using same component */}
																		<CommentableCommentsList
																			comments={r.notes || []}
																			onChange={refresh}
																		/>
																	</>
																) : (
																	<Text color="muted">-</Text>
																)}
																{r.deleted_at ? (
																	<Text color="warning">
																		<Icons.Trash title="Closed" />{" "}
																		{r.deleted_by?.name || "N/A"},{" "}
																		<RelativeTime timestamp={r.deleted_at} />
																	</Text>
																) : null}
															</Stack>
														</Stack>
													</Box>
												))}
											</Stack>
										</Box>
									) : (
										<Table
											headers={["Contact", "Details", "Tags and Follow-Ups"]
												.concat(
													params.source === "archived_only"
														? ["Close Reason"]
														: []
												)
												.concat(["Team", ""])}
											alignCols={{ 3: "right", 4: "right" }}
											hover
											bordered
											responsive
											rowKeys={items.map((r) => r.id)}
											rows={items.map((r) =>
												[
													<GuestDetails request={r} showFullDetailsInDialog />,
													<Stack gap="2" maxWidth="md" wordBreak="words">
														<Stack gap="1">
															{r.hotels_of_interest?.length ? (
																<Text fontWeight="semibold">
																	{r.hotels_of_interest
																		.map((h) => h.name)
																		.join(", ")}
																</Text>
															) : null}
															<Inline gap="px" color="muted" fontSize="sm">
																{joinAttributes(
																	r.start_date ? (
																		<Time
																			value={parseDate(
																				r.start_date,
																				"YYYY-MM-DD"
																			)}
																		/>
																	) : null,
																	r.no_of_days
																		? `${r.no_of_days - 1}N, ${r.no_of_days}D`
																		: null
																)}
															</Inline>
															<Box>
																<Badge>{r.lead_purpose?.name}</Badge>
															</Box>
														</Stack>

														{r.comments ? (
															<Inline gap="1">
																<Box>
																	<Icons.Chat />
																</Box>
																<ExpandView content={r.comments} />
															</Inline>
														) : null}
														{r.extra_fields ? (
															<ExpandView
																content={Object.entries(r.extra_fields)
																	.map(
																		([key, value]) =>
																			`${key}: ${JSON.stringify(value ?? "")}`
																	)
																	.join("\n")}
															/>
														) : null}
													</Stack>,
													<Stack gap="2">
														{r.tags?.length || r.notes?.length ? (
															<>
																<TagBadges tags={r.tags} />
																<CommentableCommentsList
																	comments={r.notes || []}
																	onChange={refresh}
																/>
															</>
														) : (
															<Text color="muted">-</Text>
														)}
													</Stack>,
												]
													.concat(
														params.source === "archived_only"
															? [
																	<Stack gap="1">
																		<Inline
																			alignItems={"center"}
																			justifyContent={"end"}
																			color={"warning"}
																			gap="1"
																		>
																			<Icons.Trash title="Closed" />
																			{r.lead_close_reason?.name || "N/A"}
																		</Inline>
																		<Text color="muted" fontSize="sm">
																			by{" "}
																			{joinAttributes(
																				r.deleted_by?.name || "N/A",
																				<RelativeTime
																					timestamp={r.deleted_at}
																				/>
																			)}
																		</Text>
																	</Stack>,
																]
															: []
													)
													.concat([
														<Box>
															<Box>
																{r.owner ? (
																	<Stack gap="1">
																		{r.owner.id !== user?.id ? (
																			<Text>{r.owner.name}</Text>
																		) : null}
																		{r.assigned_by ? (
																			<Stack>
																				<Text
																					color="muted"
																					fontWeight="semibold"
																				>
																					Assigned by
																				</Text>
																				<Text color="muted">
																					{r.assigned_by?.name}
																				</Text>
																			</Stack>
																		) : null}
																		{r.assigned_at ? (
																			<Box>
																				<RelativeTime
																					timestamp={r.assigned_at}
																				/>
																			</Box>
																		) : null}
																	</Stack>
																) : (
																	<Box>-</Box>
																)}
															</Box>
														</Box>,
													])
													.concat([<Actions request={r} onChange={refresh} />])
											)}
										/>
									)
								}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Fragment>
	)
}

const PURPOSE_LABELS: Record<string, string> = {
	quotation_request: "Quotation",
	enquiry: "Enquiry",
	group: "Group",
	mice: "MICE",
	wedding: "Wedding",
	corporate: "Corporate",
	reconfirmation: "Reconfirmation",
	amendment: "Amendment",
	cancellation: "Cancellation",
	refund: "Refund",
	complaint: "Complaint",
	payment: "Payment",
}

function formatPurpose(value: string): string {
	return PURPOSE_LABELS[value] ?? value
}

function GuestDetails({
	request: r,
	showFullDetailsInDialog,
}: {
	request: ITripPlanRequest
	showFullDetailsInDialog?: boolean
}) {
	const [ref, setRef] = useCallbackRef<HTMLDivElement>()
	return (
		<Inline gap="2" flexWrap="wrap">
			<Stack gap="2" flex="1">
				<Stack gap="2">
					<Inline gap="2" flexWrap="wrap">
						{r.lead_channel ? (
							<Inline
								alignItems="center"
								color="muted"
								gap="1"
								fontSize="sm"
								flexWrap="wrap"
							>
								{r.lead_channel.lead_channel_category ? (
									<LeadChannelIcon
										category={r.lead_channel.lead_channel_category}
									/>
								) : null}
								<Text>{r.lead_channel.name}</Text>
							</Inline>
						) : null}
						{r.is_ai_generated ? (
							<Badge
								status="primary"
								fullRounded
								title="Auto-created by the AI email parser"
							>
								<Icons.SparklesSolid size="3" /> AI
							</Badge>
						) : null}
						{typeof r.extra_fields?.purpose === "string" &&
						r.extra_fields.purpose &&
						r.extra_fields.purpose !== "quotation_request" &&
						r.extra_fields.purpose !== "enquiry" ? (
							<Badge
								fullRounded
								outlined
								title={`Parser query type: ${r.extra_fields.purpose}`}
							>
								{formatPurpose(r.extra_fields.purpose)}
							</Badge>
						) : null}
						<LinkedWhatsAppBadge request={r} />
					</Inline>
					<Inline
						gap="1"
						alignItems="baseline"
						justifyContent="between"
						ref={(ref) => setRef(ref)}
					>
						<Inline gap="2" alignItems="center">
							<TripSourceBadge request={r} />
							<LeadDetailsDialog request={r}>
								{({ open }) => (
									<a
										href="#view-details"
										onClick={(e) => {
											e.preventDefault()
											if (showFullDetailsInDialog) {
												open()
											}
										}}
									>
										<Heading
											as="h4"
											fontSize="md"
											color={showFullDetailsInDialog ? "accent" : undefined}
										>
											{r.trip_source_contact
												? r.trip_source_contact.name
												: r.name}
										</Heading>
									</a>
								)}
							</LeadDetailsDialog>
							{r.no_of_adults || r.no_of_children ? (
								<Text fontWeight="semibold">
									(
									{[
										r.no_of_adults ? `${r.no_of_adults}A` : null,
										r.no_of_children ? `${r.no_of_children}C` : null,
									]
										.filter(Boolean)
										.join(" + ")}
									)
								</Text>
							) : null}
							<PhoneNumber
								value={
									r.trip_source_contact
										? r.trip_source_contact.phone_number
										: r.phone_number
								}
								tooltipContainer={ref}
								callLoggableId={r.id}
								callLoggableType="trip_plan_requests"
								contactName={r.name}
								iconOnly
							/>
						</Inline>
					</Inline>
				</Stack>
				<Stack gap="px">
					<Inline gap="2" alignItems="center" flexWrap="wrap">
						{r.trip_source_contact?.phone_number || r.phone_number ? (
							<Box>
								<PhoneNumber
									tooltipContainer={ref}
									value={
										r.trip_source_contact
											? r.trip_source_contact.phone_number
											: r.phone_number
									}
								/>
							</Box>
						) : null}
						{r.trip_source_contact?.email || r.email ? (
							<Box
								borderLeftColor="muted"
								borderLeftWidth={
									(r.trip_source_contact?.phone_number || r.phone_number) &&
									(r.trip_source_contact?.email || r.email)
										? "2"
										: "0"
								}
								paddingLeft={
									(r.trip_source_contact?.phone_number || r.phone_number) &&
									(r.trip_source_contact?.email || r.email)
										? "2"
										: "0"
								}
							>
								<Email
									tooltipContainer={ref}
									value={
										r.trip_source_contact
											? r.trip_source_contact.email
											: r.email
									}
								/>
							</Box>
						) : null}
					</Inline>
					{r.created_at && (
						<Text
							fontSize="base"
							color={"muted"}
							style={{ fontStyle: "italic" }}
						>
							<RelativeTime timestamp={r.created_at} />
						</Text>
					)}
				</Stack>
			</Stack>
			{r.has_past_interactions?.calls || r.has_past_interactions?.mails ? (
				<PastInteractionsInDialog
					requestId={r.id}
					hasPastInteractions={r.has_past_interactions}
				>
					{({ open }) => (
						<Button level="secondary" onClick={open} status="primary">
							<Icons.Message />
						</Button>
					)}
				</PastInteractionsInDialog>
			) : null}
		</Inline>
	)
}

/**
 * The lead's WhatsApp messages across its linked conversations, counted like
 * its emails, opening the conversations in a dialog for a quick chat without
 * leaving the listing.
 */
function LinkedWhatsAppBadge({ request: r }: { request: ITripPlanRequest }) {
	const [open, setOpen] = useState(false)
	if (!r.wa_messages_count) {
		return null
	}
	return (
		<>
			<Badge fullRounded outlined status="success">
				<a
					href="#linked-whatsapp"
					onClick={(e) => {
						e.preventDefault()
						setOpen(true)
					}}
					title="WhatsApp messages"
				>
					<Icons.WhatsApp size="3" /> {r.wa_messages_count}
				</a>
			</Badge>
			<Dialog
				open={open}
				onClose={() => setOpen(false)}
				xl
				title="WhatsApp Conversations"
			>
				<Box style={{ height: "75vh", minHeight: 0 }}>
					{open ? (
						<WhatsAppLinkedConversations
							modelType="trip_plan_requests"
							modelId={Number(r.id)}
							emptyStateMessage="No WhatsApp conversations are linked to this lead yet."
						/>
					) : null}
				</Box>
			</Dialog>
		</>
	)
}

function Actions({
	request: r,
	onChange: refresh,
}: {
	request: ITripPlanRequest
	onChange: () => void
}) {
	const xhr = useXHR()
	const navigate = useNavigate()
	const { user } = useAuthUser()
	const canSendEmail = Boolean(user?.reply_mailboxes_count) && !r.deleted_at

	return (
		<Inline justifyContent="end" alignItems="center" gap="2">
			{r.trip_id ? (
				<Link
					to={`/trips/${r.trip_id}`}
					color="accent"
					fontWeight="semibold"
					anchored
				>
					#&nbsp;{r.trip_id}
				</Link>
			) : !r.deleted_at ? (
				<CreateNewQueryFromRequestInDialog
					request={r}
					onMovedToTrip={(tripId) =>
						navigate("/trips/:tripId/new-quote", {
							params: { tripId: String(tripId) },
						})
					}
					onChange={refresh}
				>
					{({ create }) => (
						<Button
							title={
								getLinkedTripsForRequest(r).length > 0
									? "Attach to Linked Trip"
									: "Create New Query"
							}
							size="sm"
							onClick={create}
						>
							<Icons.Plus />
						</Button>
					)}
				</CreateNewQueryFromRequestInDialog>
			) : null}
			<Box>
				<Component initialState={false}>
					{({ state: isEditing, setState: changeIsEditing }) => (
						<>
							<Dialog
								open={isEditing}
								onClose={() => changeIsEditing(false)}
								title="Assign Team Member"
								sm
							>
								<Dialog.Body>
									<Form<{ owner: typeof r.owner }>
										initialValues={{
											owner: r.owner,
										}}
										validate={validateFormValues(
											Validator.object().shape({
												owner: Validator.object()
													.nullable()
													.typeError("Please select a user to assign."),
											})
										)}
										onSubmit={withServerErrors(async (values) => {
											const { owner } = values
											await LeadXHR(xhr).assignLeadOwner([r.id], owner?.id)
											changeIsEditing(false)
											refresh()
											if (!owner) {
												showSnackbar("Team member remove from the request.")
											} else {
												showSnackbar("Team member assigned to the request.")
											}
										})}
										subscription={{
											submitting: true,
										}}
									>
										{({ submitting, handleSubmit }) => (
											<form noValidate onSubmit={handleSubmit}>
												<SelectField
													select={SelectUsers}
													name="owner"
													fetchOnMount
													multiple={false}
													label="Select Team Member"
												/>
												<Divider sm />
												<Stack gap="4">
													<SubmissionError />
													<Inline gap="4">
														<Button type="submit" disabled={submitting}>
															{submitting ? "Updating..." : "Assign"}
														</Button>
														<Button
															type="button"
															disabled={submitting}
															onClick={() => changeIsEditing(false)}
														>
															Cancel
														</Button>
													</Inline>
												</Stack>
											</form>
										)}
									</Form>
								</Dialog.Body>
							</Dialog>

							<EditTagsAndFollowUpsDialog request={r} onSuccess={refresh}>
								{({ onEdit: editTags }) => (
									<ComposeEmail
										title="Send Email"
										modelType="trip_plan_requests"
										modelId={Number(r.id)}
										onSuccess={refresh}
									>
										{({ onCompose }) => (
											<Dropdown alignRight>
												<Dropdown.ToggleButton level="tertiary" size="sm">
													<Icons.DotsVertical />
												</Dropdown.ToggleButton>
												<Dropdown.Menu>
													<Dropdown.MenuItem onClick={() => editTags()}>
														<Icons.Tags /> Edit Tags and Follow-Ups
													</Dropdown.MenuItem>
													{canSendEmail ? (
														<Dropdown.MenuItem
															onClick={(e) => {
																e.preventDefault()
																onCompose()
															}}
														>
															<Icons.Mail /> Send Email
														</Dropdown.MenuItem>
													) : null}
													{!r.trip_id ? (
														<>
															<Dropdown.MenuItem
																onClick={() => changeIsEditing(true)}
															>
																<Icons.User /> Assign Team
															</Dropdown.MenuItem>
															<Dropdown.MenuItemDivider />
															{!r.deleted_at ? (
																<CloseLeadInModal
																	request={r}
																	onSuccess={refresh}
																>
																	{({ open }) => (
																		<Dropdown.MenuItem onClick={open}>
																			<Icons.Trash color="danger" /> Close
																			Request
																		</Dropdown.MenuItem>
																	)}
																</CloseLeadInModal>
															) : (
																<Dropdown.MenuItem
																	onClick={async () => {
																		if (
																			window.confirm(
																				"Are you sure you want to restore this Close Lead ?"
																			)
																		) {
																			const { message } = await LeadXHR(
																				xhr
																			).restoreLead(r.id)
																			refresh()
																			showSnackbar(
																				message ||
																					"Request restored successfully"
																			)
																		}
																	}}
																>
																	<Icons.Refresh color="success" /> Restore
																	Request
																</Dropdown.MenuItem>
															)}
														</>
													) : null}
												</Dropdown.Menu>
											</Dropdown>
										)}
									</ComposeEmail>
								)}
							</EditTagsAndFollowUpsDialog>
						</>
					)}
				</Component>
			</Box>
		</Inline>
	)
}

function Filters() {
	const { value: type } = useFieldValue<string | undefined>("source")
	const getLeadStatusType = useMemo(
		() =>
			type === "new_only"
				? LeadStatusType.filter(
						(status) =>
							status.id === "active_followups" ||
							status.id === "pending_followups"
					)
				: LeadStatusType,
		[type]
	)

	return (
		<Stack gap="4">
			<SelectField
				label="Tags"
				name="tags"
				select={SelectTags}
				type="trips"
				fetchOnMount
				multiple
			/>
			<Stack gap="1">
				<label htmlFor="field_created_after">Created During</label>
				<Box display="flex" alignItems="center">
					<DatePickerField name="created_after" dateFormat="DD MMM, YYYY" />
					<Box>
						<Icons.SwitchHorizontal />
					</Box>
					<DatePickerField
						name="created_before"
						rightAlign
						dateFormat="DD MMM, YYYY"
					/>
				</Box>
			</Stack>
			<SelectField
				label="Team Members"
				name="owners"
				select={SelectUsers}
				multiple
			/>
			<SelectField
				label="Destinations"
				name="destinations"
				select={SelectLeadsDestinations}
				multiple
			/>
			{type === "archived_only" || type === "moved_to_trips_only" ? null : (
				<SelectField
					select={Select}
					name="leadStatus"
					label="Status Type"
					options={getLeadStatusType}
					multiple
				/>
			)}
			{type === "all" || type === "moved_to_trips_only" ? (
				<SelectField
					select={SelectTripSources}
					name="trip_sources"
					label="B2B Sources"
					multiple
				/>
			) : null}
			<SelectField
				select={SelectLeadChannels}
				name="lead_channels"
				label="Lead Channels"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={SelectLeadPurposes}
				name="lead_purposes"
				label="Lead Purposes"
				multiple
				fetchOnMount
			/>
			<SelectField
				name="hotels_of_interest"
				label="Hotels of Interest"
				select={SelectHotels}
				multiple
				fetchOnMount
				includeDisabled
			/>
			<SelectField
				select={SelectLeadCloseReasons}
				name="lead_close_reasons"
				label="Close Reasons"
				multiple
				fetchOnMount
			/>
			<SwitchInputField label="Unassigned Team" name="unassigned_only" />
			<SwitchInputField label="AI Leads" name="ai_only" />
		</Stack>
	)
}

export function SelectLeadsDestinations(
	props: Optional<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			fetch={(q) =>
				LeadXHR(xhr)
					.getLeadDestinations({ q })
					.then((resp) => resp)
			}
			{...props}
		/>
	)
}

function LeadDetails({
	request,
	showfullDetails = false,
}: {
	request: ITripPlanRequest
	showfullDetails?: boolean
}) {
	return (
		<Stack>
			<Inline gap="8" flexWrap="wrap">
				<Stack gap="1">
					<Heading fontSize="base" color="primary">
						Guest Details
					</Heading>
					<GuestDetails request={request} />
				</Stack>
				<Stack gap="1" maxWidth="sm" wordBreak="words">
					<Heading fontSize="base" color="primary">
						More Details
					</Heading>
					<Stack gap="2">
						{request.comments ? (
							<Box maxWidth="xs" whiteSpace="preserveLine">
								<ExpandView content={request.comments} />
							</Box>
						) : null}
						{request.extra_fields ? (
							<Box maxWidth="xs" whiteSpace="preserveLine">
								<ExpandView
									content={Object.entries(request.extra_fields)
										.map(
											([key, value]) => `${key}: ${JSON.stringify(value ?? "")}`
										)
										.join("\n")}
								/>
							</Box>
						) : null}
						<Box>
							<Badge success>{request.trip_source?.name}</Badge>
						</Box>
					</Stack>
				</Stack>
			</Inline>
			{showfullDetails ? (
				<>
					<Divider />
					<LeadFullDetails request={request} />
				</>
			) : null}
		</Stack>
	)
}

function LeadFullDetails({ request }: { request: ITripPlanRequest }) {
	const [type, setType] = useState<"details" | "activities">("details")
	return (
		<Tabs gap="4">
			<TabsList>
				<TabItem onClick={() => setType("details")} active={type === "details"}>
					Details
				</TabItem>
				<TabItem
					onClick={() => setType("activities")}
					active={type === "activities"}
				>
					Activities
				</TabItem>
			</TabsList>
			<TabContent paddingX="4">
				{type === "details" ? (
					<LeadBasicDetails request={request} />
				) : (
					<ActivityLogs
						subjectId={request.id}
						subjectType="trip_plan_requests"
					/>
				)}
			</TabContent>
		</Tabs>
	)
}

function LeadBasicDetails({ request }: { request: ITripPlanRequest }) {
	const [ref, setRef] = useCallbackRef<HTMLDivElement>()
	return (
		<Stack gap="4" ref={(ref) => setRef(ref)}>
			<Grid gap="6">
				<Col xs={12} md={4}>
					<Heading fontSize="lg">
						<Icons.ClipboardCopy size="6" /> Lead Details
					</Heading>
				</Col>
				<Col>
					<Grid gap="6">
						<Col xs={12} md={6}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Lead Channel
								</Text>
								<Text fontWeight="semibold">
									{request.lead_channel?.name || (
										<Text color={"muted"} fontWeight="normal">
											-
										</Text>
									)}
								</Text>
							</Stack>
						</Col>
						<Col xs={12} md={5}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Lead Purpose
								</Text>
								<Text fontWeight="semibold">
									{request.lead_purpose?.name || (
										<Text color={"muted"} fontWeight="normal">
											-
										</Text>
									)}
								</Text>
							</Stack>
						</Col>
					</Grid>
				</Col>
			</Grid>
			<Divider sm />
			<Grid gap="6">
				<Col xs={12} md={4}>
					<Heading as="h3">
						<Icons.InboxIn size="6" /> Query Source
					</Heading>
				</Col>
				<Col>
					<Grid gap="6">
						<Col xs={12} md={6}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Query Source
								</Text>
								<Text fontWeight="semibold">
									{request.trip_source?.name || "-"}
								</Text>
							</Stack>
						</Col>

						<Col xs={12} md={5}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Source Contact
								</Text>
								<Text fontWeight="semibold">
									{request.trip_source_contact?.name || "-"}
								</Text>
								<PhoneNumber
									value={request.trip_source_contact?.phone_number}
									tooltipContainer={ref}
								/>
							</Stack>
						</Col>

						<Col xs={12} md={6}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Reference ID (optional)
								</Text>
								<Text fontWeight="semibold">{request.reference_id || "-"}</Text>
								<Text color="muted" fontSize="xs">
									A custom id for your reference regarding the query
								</Text>
							</Stack>
						</Col>

						<Col xs={12} md={5}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Sales Team
								</Text>
								<Text fontWeight="semibold">{request.owner?.name || "-"}</Text>
							</Stack>
						</Col>

						<Col xs={12} md={6}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Tags (optional)
								</Text>
								{request.tags && request.tags.length > 0 ? (
									<Inline gap="1" flexWrap="wrap">
										{request.tags.map((tag) => (
											<Badge key={tag.id}>{tag.name}</Badge>
										))}
									</Inline>
								) : (
									<Text fontWeight="semibold">-</Text>
								)}
							</Stack>
						</Col>
						<Col xs={12} md={5}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Hotels of Interest
								</Text>
								{request.hotels_of_interest &&
								request.hotels_of_interest.length > 0 ? (
									<Inline gap="1" flexWrap="wrap">
										{request.hotels_of_interest.map((hotel) => (
											<Badge key={hotel.id}>{hotel.name}</Badge>
										))}
									</Inline>
								) : (
									<Text fontWeight="semibold">-</Text>
								)}
							</Stack>
						</Col>
					</Grid>
				</Col>
			</Grid>
			<Divider sm />
			<Grid gap="6">
				<Col xs={12} md={4}>
					<Heading as="h3">
						<Icons.LocationMarker size="6" /> Destination and Duration
					</Heading>
				</Col>
				<Col>
					<Grid gap="6">
						<Col xs={12} md={6}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Destinations
								</Text>
								<Text fontWeight="semibold">{request.destination || "-"}</Text>
							</Stack>
						</Col>

						<Col xs={12} md={5}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Start Date
								</Text>
								{request.start_date ? (
									<Text fontWeight="semibold">
										<Time value={parseDate(request.start_date, "YYYY-MM-DD")} />
									</Text>
								) : (
									<Text fontWeight="semibold">-</Text>
								)}
							</Stack>
						</Col>

						<Col xs={12} md={6}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									No. of Nights
								</Text>
								<Text fontWeight="semibold">
									{request.no_of_days
										? `${request.no_of_days - 1} Nights, ${request.no_of_days} Days`
										: "-"}
								</Text>
							</Stack>
						</Col>

						<Col xs={12} md={5}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									No. of Adults
								</Text>
								<Text fontWeight="semibold">{request.no_of_adults || "-"}</Text>
							</Stack>
						</Col>

						<Col xs={12} md={6}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Children and their Ages
								</Text>
								<Text fontWeight="semibold">
									{request.no_of_children
										? `${request.no_of_children} ${
												request.children && request.children.length > 0
													? ` (${request.children
															.map((c) =>
																c.count > 1
																	? `${c.count} x ${c.age} yrs`
																	: `${c.age} yrs`
															)
															.join(", ")})`
													: ""
											}`
										: "-"}
								</Text>
							</Stack>
						</Col>
					</Grid>
				</Col>
			</Grid>
			<Divider sm />
			<Grid gap="6">
				<Col xs={12} md={4}>
					<Heading as="h3">
						<Icons.Identification size="6" /> Guest Details
					</Heading>
				</Col>
				<Col>
					<Grid gap="6">
						<Col xs={12} md={6}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Name
								</Text>
								<Text fontWeight="semibold">{request.name || "-"}</Text>
							</Stack>
						</Col>

						<Col xs={12} md={5}>
							<Stack gap="1" style={{ minWidth: "150px", flex: 1 }}>
								<Text color="muted" fontSize="sm">
									Phone Number(s)
								</Text>
								{request.phone_number ? (
									<PhoneNumber
										color="black"
										tooltipContainer={ref}
										fontWeight="semibold"
										value={request.phone_number}
									/>
								) : (
									<Text fontWeight="semibold">-</Text>
								)}
							</Stack>
						</Col>

						<Col xs={12} md={6}>
							<Stack gap="1">
								<Text color="muted" fontSize="sm">
									Email
								</Text>
								{request.email ? (
									<Email
										color="black"
										fontWeight="semibold"
										tooltipContainer={ref}
										value={request.email}
									/>
								) : (
									<Text fontWeight="semibold">-</Text>
								)}
							</Stack>
						</Col>
					</Grid>
				</Col>
			</Grid>
			<Divider sm />
			<Grid gap="6">
				<Col xs={12} md={4}>
					<Heading as="h3">
						<Icons.Annotation size="6" /> Comments or Notes
					</Heading>
				</Col>
				<Col>
					<Grid gap="6">
						<Col xs={12}>
							<Stack gap="1">
								<Text color="muted" fontSize="sm">
									Comments (optional)
								</Text>
								{request.comments ? (
									<Box whiteSpace="preserveLine" fontWeight="semibold">
										{request.comments}
									</Box>
								) : (
									<Text fontWeight="semibold">-</Text>
								)}
							</Stack>
						</Col>

						<Col xs={12}>
							{request.extra_fields ? (
								<Stack gap="1">
									<Text color="muted" fontSize="sm">
										Extra Fields
									</Text>
									{Object.keys(request.extra_fields).map((key) => (
										<Text key={key} fontWeight="semibold">
											{key}:{" "}
											{
												JSON.stringify(
													(request.extra_fields?.[key] as never) || ""
												) as string
											}
										</Text>
									))}
								</Stack>
							) : null}
						</Col>
					</Grid>
				</Col>
			</Grid>
		</Stack>
	)
}

function getLinkedTripsForRequest(request: ITripPlanRequest) {
	// Existing Trips reached via the lead's shared email thread(s) — e.g. an
	// amendment lead for an existing Trip. Skip the lead's own converted Trip.
	return (request.linked_mail_threaded_trips || []).filter(
		(t) => t.id !== request.trip_id
	)
}

export function CreateNewQueryFromRequestInDialog({
	children,
	request,
	onMovedToTrip,
	onChange,
}: {
	request: ITripPlanRequest
	children: (props: { create: () => void }) => React.ReactNode
	onMovedToTrip: (tripId: number) => void
	onChange: () => void
}) {
	const [isOpenDialog, openDialog, closeDialog] = useDialog()
	// Maintaining current request in state to avoid re-rendering of the dialog in case the underlying list is updated.
	const [currentRequest, setCurrentRequest] = useState<ITripPlanRequest | null>(
		null
	)
	return (
		<>
			{children({
				create: () => {
					setCurrentRequest(request)
					openDialog()
				},
			})}
			<Dialog
				open={isOpenDialog}
				onClose={closeDialog}
				title={
					getLinkedTripsForRequest(request).length > 0
						? "Attach to Linked Trip"
						: "Create New Query from Request"
				}
				lg
			>
				<Dialog.Body>
					{currentRequest ? (
						<CreateNewQueryFromRequest
							request={currentRequest}
							onMovedToTrip={(tripId) => {
								setCurrentRequest(null)
								closeDialog()
								onMovedToTrip(tripId)
							}}
							onCancel={() => {
								setCurrentRequest(null)
								closeDialog()
							}}
							onUpdated={() => {
								setCurrentRequest(null)
								closeDialog()
								onChange()
							}}
						/>
					) : null}
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function CreateNewQueryFromRequest({
	request,
	onMovedToTrip,
	onCancel,
	onUpdated,
}: {
	request: ITripPlanRequest
	onMovedToTrip: (tripId: number) => void
	onCancel: () => void
	onUpdated: () => void
}) {
	const xhr = useXHR()
	const requestId = useId()
	const { data } = useSWR(
		`trip-plan-request-to-trip/${request.id}?${requestId}`,
		() =>
			LeadXHR(xhr)
				.getPayloadForTrip(request.id)
				.then((payload) => {
					const parsedPhoneNumbers = payload.tourist?.phone_numbers?.length
						? payload.tourist.phone_numbers.map((number) =>
								parsePhoneNumber(number, "IN")
							)
						: []
					return {
						can_create_new: payload.can_create_new,
						reference_id: payload.reference_id || "",
						start_date: payload.start_date
							? startOf(parseDate(payload.start_date, "YYYY-MM-DD"), "day")
							: undefined,
						no_of_days: payload.no_of_days,
						no_of_nights: payload.no_of_nights,
						destinations: payload.destinations || [],
						no_of_adults: payload.no_of_adults,
						children: payload.children || [],
						trip_source: payload.trip_source || undefined,
						trip_source_contact: payload.trip_source_contact || undefined,
						comments: payload.comments || "",
						tourist: {
							name: payload.tourist?.name || "",
							salutation: payload.tourist?.salutation || "Mr.",
							email: payload.tourist?.email || "",
							phone_numbers: parsedPhoneNumbers
								.map((parsedPhoneNumber) =>
									parsedPhoneNumber?.nationalNumber
										? {
												is_primary: true,
												country_code: parsedPhoneNumber?.country,
												number: parsedPhoneNumber?.nationalNumber,
												phone_number: parsedPhoneNumber?.number,
											}
										: undefined
								)
								.filter((p): p is NonNullable<typeof p> => Boolean(p))[0] || {
								is_primary: true,
								country_code: "IN",
								number: "",
								phone_number: "",
							},
							address: payload.tourist?.address
								? transformAddressToEditableValue(payload.tourist.address)
								: undefined,
							nationality: payload.tourist?.nationality || undefined,
						},
						tags: request.tags || [],
						lead_channel: payload.lead_channel || request.lead_channel || null,
						lead_purpose: payload.lead_purpose || request.lead_purpose || null,
						hotels_of_interest: payload.hotels_of_interest || null,
						is_agent: payload.trip_source
							? ((payload.trip_source.is_agent ? "1" : "0") as "0" | "1")
							: undefined,
						sales_team: payload.sales_team,
						link_to_existing_trip: (payload.is_manually_linked_to_trip
							? "1"
							: "0") as "0" | "1",
						trip: payload.trip,
						trip_id: payload.trip ? String(payload.trip?.id) : "",
						suggested_trips: payload.suggested_trips,
					}
				})
	)

	if (!data) {
		return <Spinner padding="4" alignCenter />
	}

	return (
		<LeadDetailsForm
			mode="edit"
			showTeam
			initialValues={data}
			disableCreateNew={!data.can_create_new}
			onCancel={onCancel}
			onSubmit={async (payload, intent) => {
				const move_to_trips = intent === "MOVE_TO_TRIP"
				const resp = await LeadXHR(xhr).updateLead(request.id, {
					...payload,
					move_to_trips: move_to_trips ? 1 : undefined,
				})
				if (move_to_trips && resp.trip_id) {
					showSnackbar("Trip created successfully")
					onMovedToTrip(resp.trip_id)
					return
				}
				if (payload.link_to_existing_trip === 1) {
					showSnackbar("Lead Linked successfully.")
				} else {
					showSnackbar("Lead Updated successfully.")
				}
				onUpdated()
			}}
		/>
	)
}

function TabLink({
	params,
	setParams,
	children,
	source,
}: {
	params: TFilters
	setParams: (params: TFilters) => void
	children: React.ReactNode
	showCount?: boolean
	extraParams?: unknown
	source?: string
}) {
	return (
		<li className={(source ? params.source === source : true) ? "active" : ""}>
			<a
				href={`#${source}`}
				onClick={(e) => {
					e.preventDefault()
					const newParams = { ...params }
					if (source) {
						newParams.source = source
					}
					setParams(newParams)
				}}
			>
				{children}
			</a>
		</li>
	)
}

const EditTagsAndFollowUpsValidation = validateFormValues(
	Validator.object().shape({
		tags: Validator.array().of(
			Validator.object().shape({
				name: Validator.string().required(),
			})
		),
		due_at: Validator.date()
			.nullable()
			// The WhatsApp message is sent at the due time, so without one
			// there is no moment at which it would go out.
			.when("wa_template", {
				is: (value: unknown) => Boolean(value),
				then: (schema) =>
					schema.required(
						"Due date is required when a WhatsApp message is attached"
					),
			}),
		note: Validator.string()
			.trim()
			.min(3, "Follow up Note must be at least 3 characters")
			.max(500, "Follow up Note cannot be more than 500 characters")
			.when("due_at", {
				is: (due_at: Date) => due_at,
				then: (schema) =>
					schema.required("Follow up note is required when due date is set"),
				otherwise: (schema) => schema.nullable(),
			}),
	})
)

function EditTagsAndFollowUpsDialog({
	children,
	request,
	onSuccess,
}: {
	children: (props: { onEdit: () => void }) => React.ReactNode
	request: ITripPlanRequest
	onSuccess: () => void
}) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({ onEdit: open })}
			<Dialog open={isOpen} onClose={close} title="Edit Tags and Follow-Ups" sm>
				<Dialog.Body>
					<EditTagsAndFollowUpsForm
						request={request}
						onCancel={close}
						onSuccess={() => {
							close()
							onSuccess()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function EditTagsAndFollowUpsForm({
	request,
	onCancel,
	onSuccess,
}: {
	request: ITripPlanRequest
	onSuccess: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	return (
		<Form<IEditTagsAndFollowUpsFormValues>
			initialValues={{
				tags: request.tags?.map((t) => ({ id: t.id, name: t.name })) || [],
			}}
			validate={EditTagsAndFollowUpsValidation}
			onSubmit={withServerErrors(async (values) => {
				await LeadXHR(xhr).editTagsAndFollowUps(request.id, {
					note: values.note,
					tags: values.tags?.map((t) => t.name),
					due_at: values.due_at ? dateToUTCString(values.due_at) : undefined,
					...whatsAppReminderPayload(values),
				})
				showSnackbar("Tags and Follow-Ups updated successfully")
				onSuccess()
			})}
			mutators={{ ...arrayMutators }}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<SelectField
							select={SelectTags}
							name="tags"
							fetchOnMount
							label="Select tags"
							type={"trips"}
							multiple
							creatable={false}
						/>

						<Inline gap="2" width="full" flexWrap={"wrap"}>
							<Box flex="1" minWidth="max">
								<TextAreaInputField
									label="Follow Up Note"
									name="note"
									placeholder="Enter follow up note"
									rows={3}
									help="Maximum of 500 characters"
									width="full"
								/>
							</Box>

							<DatePickerField
								label="Due Date"
								name="due_at"
								dateFormat="MMMM D, YYYY"
								help="If it needs to be resolved at a specific time"
								timeFormat
							/>
						</Inline>

						<WhatsAppReminderFields
							commentableType="trip_plan_requests"
							commentableId={request.id}
						/>

						<Stack gap="4">
							<SubmissionError />
							<Inline gap="4">
								<Button disabled={submitting} type="submit">
									{submitting ? "Saving..." : "Save"}
								</Button>
								{onCancel ? (
									<Button onClick={onCancel} disabled={submitting}>
										Cancel
									</Button>
								) : null}
							</Inline>
						</Stack>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function BulkAssignLeads({
	children,
	params,
	onSuccess,
	openAdvancedFilters,
}: {
	children: (props: { onAssign: () => void }) => React.ReactNode
	params: Record<string, unknown>
	onSuccess: () => void
	openAdvancedFilters: () => void
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	const xhr = useXHR()
	const [items, setItems] = useState<ITripPlanRequest[]>([])
	const [selectedIds, setSelectedIds] = useState<number[]>([])
	const [isSelectAll, setIsSelectAll] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [totalCount, setTotalCount] = useState(0)
	const [currentPage, setCurrentPage] = useState(1)
	const [batchId, setBatchId] = useState<string | null>(null)
	const hasMore = useRef(false)

	const areFiltersApplied = useMemo(
		() =>
			checkFilterAppliedFn(
				params,
				"page",
				"trip_sources",
				"source",
				"new_only",
				"archived_only"
			),
		[params]
	)

	const selectedCount = isSelectAll
		? totalCount - selectedIds.length
		: selectedIds.length

	const fetchRequests = useCallback(
		async (page: number, reset?: boolean) => {
			setIsLoading(true)
			try {
				const resp = await LeadXHR(xhr).getLeads({
					...params,
					page,
					limit: 20,
				})
				setItems((prev) => (reset ? resp.data : [...prev, ...resp.data]))
				setTotalCount(resp.meta?.total || resp.data.length)
				hasMore.current = Boolean(
					resp.meta?.last_page && resp.meta.last_page > page
				)
				setCurrentPage(page)
			} catch (e) {
				console.error(e)
			} finally {
				setIsLoading(false)
			}
		},
		[xhr, params]
	)

	useEffect(() => {
		if (isOpen && areFiltersApplied) {
			fetchRequests(1, true)
		}
		if (!isOpen) {
			setItems([])
			setSelectedIds([])
			setIsSelectAll(false)
			setTotalCount(0)
			setCurrentPage(1)
		}
	}, [isOpen, areFiltersApplied, fetchRequests])

	useEffect(() => {
		if (!batchId) return

		let isMounted = true
		showSnackbar("Assigning Leads...")

		const intervalId = window.setInterval(async () => {
			try {
				const resp = await xhr.get<{ data: { finishedAt: string | null } }>(
					`batches/${batchId}`
				)
				if (resp.data.data.finishedAt) {
					clearInterval(intervalId)
					if (isMounted) {
						showSnackbar("Leads assigned successfully!")
						setBatchId(null)
						closeDialog()
						onSuccess()
					}
				}
			} catch (e) {
				console.error("Batch polling failed:", e)
			}
		}, 1000)

		return () => {
			isMounted = false
			clearInterval(intervalId)
		}
	}, [batchId, xhr, closeDialog, onSuccess])

	const openFilter = () => {
		closeDialog()
		openAdvancedFilters()
	}

	const toggleItem = (id: number) => {
		setSelectedIds((ids) =>
			ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]
		)
	}

	const isItemSelected = (id: number) =>
		isSelectAll ? !selectedIds.includes(id) : selectedIds.includes(id)

	return (
		<>
			{children({ onAssign: () => openDialog() })}
			<Dialog open={isOpen} onClose={closeDialog} title="Bulk Assign Leads" lg>
				<Form<{ confirmed_count: number; sales_owner_id: IUser }>
					validate={validateFormValues(
						Validator.object().shape({
							confirmed_count: Validator.number()
								.equals(
									[selectedCount],
									`Please type ${selectedCount} to confirm the selection`
								)
								.not([0], "Please type a valid number")
								.required(
									`Please type ${selectedCount} to confirm the selection`
								),
							sales_owner_id: Validator.object().required(
								"Please select a sales person to assign"
							),
						})
					)}
					onSubmit={withServerErrors(async ({ sales_owner_id }) => {
						const resp = await xhr.post(
							"/trip-plan-request-owners/bulk-assign",
							{
								...params,
								owner_id: sales_owner_id.id,
								trip_requests_count: selectedCount,
								...(isSelectAll
									? {
											excluded_ids: selectedIds,
										}
									: {
											included_ids: selectedIds,
										}),
							}
						)
						setBatchId(resp.data.data.id)
					})}
					subscription={{ submitting: true }}
				>
					{({ submitting, handleSubmit }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body padding="8">
								<Inline justifyContent={"between"} gap={"2"}>
									<Text fontSize="lg" fontWeight="semibold">
										Please review details
										{areFiltersApplied ? ` (${totalCount} total)` : null}
									</Text>
									{!isSelectAll && areFiltersApplied && items.length > 0 ? (
										<Inline
											cursor="pointer"
											as="label"
											alignItems="center"
											gap="2"
										>
											<CheckboxInput
												checked={isSelectAll}
												onChange={() => {
													setIsSelectAll(true)
													setSelectedIds([])
												}}
												name="select_all"
												id="select_all_tpr"
											/>
											<Box htmlFor="select_all_tpr" as="label">
												Select All
											</Box>
										</Inline>
									) : null}
								</Inline>

								<Stack
									padding="4"
									borderWidth="1"
									borderColor="warning"
									rounded="lg"
									marginY="4"
								>
									<Box style={{ height: "300px" }} overflowY="auto" as="div">
										{!areFiltersApplied ? (
											<Box
												display="flex"
												alignItems="center"
												justifyContent="center"
												flexDirection="col"
												gap="4"
												style={{ height: "100%" }}
											>
												<Box as="p">
													Please choose right filter or search option to assign
												</Box>
												<Button onClick={() => openFilter()} type="button">
													Close and Apply Filters
												</Button>
											</Box>
										) : !items.length && !isLoading ? (
											<Box
												display="flex"
												alignItems="center"
												justifyContent="center"
												style={{ height: "100%" }}
											>
												<Text color="muted">
													No Leads found for the current filters.
												</Text>
											</Box>
										) : (
											<Table
												bordered
												responsive
												hover
												headers={[" ", "Name", "Contact", "Details"]}
												rows={items.map((r) => [
													<CheckboxInput
														key={r.id}
														id={`tpr_${r.id}`}
														checked={isItemSelected(r.id)}
														onChange={() => toggleItem(r.id)}
													/>,
													<Stack key={`name_${r.id}`} gap="px">
														<Text fontWeight="semibold">{r.name}</Text>
														{r.trip_source ? (
															<Text fontSize="sm" color="muted">
																{r.trip_source.name}
															</Text>
														) : null}
													</Stack>,
													<Stack key={`contact_${r.id}`} gap="px">
														{r.phone_number ? (
															<PhoneNumber value={r.phone_number} />
														) : null}
														{r.email ? <Email value={r.email} /> : null}
													</Stack>,
													<Stack key={`details_${r.id}`} gap="px">
														{r.destination ? (
															<Text>{r.destination}</Text>
														) : null}
														{r.start_date ? (
															<Text fontSize="sm" color="muted">
																{r.start_date}
																{r.no_of_days ? ` • ${r.no_of_days}D` : null}
															</Text>
														) : null}
														{r.owner ? (
															<Text fontSize="sm" color="muted">
																<Icons.User /> {r.owner.name}
															</Text>
														) : null}
													</Stack>,
												])}
											/>
										)}
										{isLoading ? (
											<Box textAlign="center" paddingY="4">
												<Spinner />
											</Box>
										) : null}
										{hasMore.current && !isLoading ? (
											<Box textAlign="center" paddingY="2">
												<Button
													onClick={() => fetchRequests(currentPage + 1)}
													level="tertiary"
													size="sm"
												>
													Load More
												</Button>
											</Box>
										) : null}
									</Box>
								</Stack>

								{areFiltersApplied && items.length > 0 ? (
									<Inline gap={"4"} flexWrap={"wrap"}>
										<SelectField
											name="sales_owner_id"
											select={SelectUsers}
											label="Choose Sales Person"
											fetchOnMount
										/>
										{selectedCount > 0 ? (
											<TextInputField
												label={`Please type ${selectedCount} to confirm`}
												name="confirmed_count"
												type="number"
												aria-label="Enter the count to confirm assignment"
												required
											/>
										) : null}
									</Inline>
								) : null}
							</Dialog.Body>
							{areFiltersApplied && items.length > 0 ? (
								<Dialog.Footer>
									<Stack gap={"4"}>
										<SubmissionError />
										<Inline gap={"2"}>
											<Button
												disabled={submitting || !selectedCount || !!batchId}
												type="submit"
												level="primary"
											>
												{submitting || !!batchId
													? "Assigning..."
													: `Assign (${selectedCount})`}
											</Button>
											<Button
												disabled={submitting || !!batchId}
												onClick={() => closeDialog()}
												level="tertiary"
											>
												Cancel
											</Button>
										</Inline>
									</Stack>
								</Dialog.Footer>
							) : null}
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

function ExpandView({
	content,
	maxLength = 100,
}: {
	content?: string
	maxLength?: number
}) {
	const isLong = (content?.length ?? 0) > maxLength

	if (!isLong) {
		return <Text style={{ whiteSpace: "pre-wrap" }}>{content}</Text>
	}

	return (
		<Component initialState={false}>
			{({ state, setState }) =>
				state ? (
					<Stack>
						<Text style={{ whiteSpace: "pre-wrap" }}>{content}</Text>
						<Box>
							<Button inline onClick={() => setState(!state)}>
								<Text as="span" textDecoration="underline" color="accent">
									View less
								</Text>
							</Button>
						</Box>
					</Stack>
				) : (
					<Stack>
						<Text>{content?.slice(0, maxLength)}...</Text>
						<Box>
							<Button inline onClick={() => setState(!state)}>
								<Text as="span" color="accent" textDecoration="underline">
									View More
								</Text>
							</Button>
						</Box>
					</Stack>
				)
			}
		</Component>
	)
}

function LeadDetailsDialog({
	request,
	children,
}: {
	request: ITripPlanRequest
	children: (props: { open: () => void }) => React.ReactNode
}) {
	const [isOpenDialog, openDialog, closeDialog] = useDialog()
	const [currentRequest, setCurrentRequest] = useState<ITripPlanRequest | null>(
		null
	)
	return (
		<>
			{children({
				open: () => {
					setCurrentRequest(request)
					openDialog()
				},
			})}
			<Dialog
				open={isOpenDialog}
				onClose={() => {
					closeDialog()
					setCurrentRequest(null)
				}}
				title="Lead Details"
				lg
			>
				<Dialog.Body>
					{currentRequest ? (
						<LeadDetails request={currentRequest} showfullDetails />
					) : null}
				</Dialog.Body>
				<Dialog.Footer>
					<Button
						onClick={() => {
							closeDialog()
							setCurrentRequest(null)
						}}
					>
						OK
					</Button>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}

function CloseLeadInModal({
	children,
	request,
	onSuccess,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	request: React.ComponentProps<typeof CloseLeadForm>["request"]
	onSuccess: () => void
}) {
	const [isOpenDialog, openDialog, closeDialog] = useDialog()
	const [currentRequest, setCurrentRequest] = useState<
		React.ComponentProps<typeof CloseLeadForm>["request"] | null
	>(null)
	const xhr = useXHR()
	return (
		<>
			{children({
				open: () => {
					setCurrentRequest(request)
					openDialog()
				},
			})}
			<Dialog
				open={isOpenDialog}
				onClose={() => {
					setCurrentRequest(null)
					closeDialog()
				}}
				title="Close Lead"
			>
				<Dialog.Body>
					{currentRequest ? (
						<CloseLeadForm
							request={currentRequest}
							onCancel={() => {
								setCurrentRequest(null)
								closeDialog()
							}}
							onSubmit={async (data) => {
								const { message } = await LeadXHR(xhr).closeLead({
									id: currentRequest.id,
									lead_channel: data.lead_channel.id,
									lead_purpose: data.lead_purpose.id,
									lead_close_reason: data.lead_close_reason.id,
								})
								closeDialog()
								onSuccess()
								showSnackbar(message || "Lead Closed successfully.")
							}}
						/>
					) : null}
				</Dialog.Body>
			</Dialog>
		</>
	)
}

interface ICloseLeadFormValues {
	lead_channel?: { id: number; name: string }
	lead_purpose?: { id: number; name: string }
	lead_close_reason?: { id: number; name: string }
}

function CloseLeadForm({
	request,
	onCancel,
	onSubmit,
}: {
	request: Pick<ITripPlanRequest, "id" | "lead_channel" | "lead_purpose">
	onCancel: () => void
	onSubmit: (data: Required<ICloseLeadFormValues>) => Promise<void>
}) {
	const initialValues: ICloseLeadFormValues = {
		lead_channel: request?.lead_channel,
		lead_purpose: request?.lead_purpose,
	}
	const validate = validateFormValues(
		Validator.object().shape({
			lead_channel: Validator.object().required("Lead channel is required"),
			lead_purpose: Validator.object().required("Lead purpose is required"),
			lead_close_reason: Validator.object().required(
				"Close reason is required"
			),
		})
	)
	return (
		<Form<ICloseLeadFormValues>
			validate={validate}
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				if (
					values.lead_channel &&
					values.lead_purpose &&
					values.lead_close_reason
				) {
					await onSubmit({
						lead_channel: values.lead_channel,
						lead_purpose: values.lead_purpose,
						lead_close_reason: values.lead_close_reason,
					})
				}
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Grid noGutters gap="4">
						<Col>
							<GetFieldValue<TLeadChannel | null> name="lead_channel">
								{({ value: leadChannel }) => (
									<SelectField
										name="lead_channel"
										label="Lead Channel"
										select={SelectLeadChannels}
										fetchOnMount
										disabled={leadChannel?.is_auto === 1}
									/>
								)}
							</GetFieldValue>
						</Col>
						<Col>
							<SelectField
								name="lead_purpose"
								label="Lead Purpose"
								select={SelectLeadPurposes}
								fetchOnMount
							/>
						</Col>
					</Grid>
					<Divider marginY={"4"} />
					<Grid gap="2">
						<Col xs={12} md={5}>
							<Stack gap="1">
								<Text fontSize={"md"} fontWeight="semibold">
									Close Reason
								</Text>
								<Text color="muted" fontSize="sm">
									The new Lead will be resolved after it is linked successfully.
								</Text>
							</Stack>
						</Col>
						<Col>
							<SelectField
								name="lead_close_reason"
								label="Close Reason"
								select={SelectLeadCloseReasons}
								fetchOnMount
							/>
						</Col>
					</Grid>

					<Divider marginY={"6"} />
					<Stack gap="4">
						<SubmissionError />
						<Inline gap="4">
							<Button
								type="submit"
								status="danger"
								level="primary"
								disabled={submitting}
							>
								<Icons.Trash size="4" />
								{submitting ? "Closing..." : "Close"}
							</Button>
							<Button onClick={onCancel} disabled={submitting}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export function SelectLeadCloseReasons(
	props: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			fetch={(q) => LeadXHR(xhr).getLeadCloseReasons({ q })}
			{...props}
		/>
	)
}

function LeadChannelIcon({
	category,
}: {
	category: TLeadChannel["lead_channel_category"]
}) {
	switch (category) {
		case "WhatsApp":
			return <Icons.WhatsApp color="success" />
		case "Email":
			return <Icons.Mail color="primary" />
		case "Call":
			return <Icons.Phone color="primary" />
		case "Website":
			return <Icons.Globe />
		case "Ads":
			return <Icons.AD />
		case "DMs":
			return <Icons.DM />
		case "Walk-in":
			return <Icons.WalkIn size="6" />
		case "Others":
			return <Icons.Shapes />
		default:
			return <Icons.Shapes />
	}
}

function TripSourceBadge({ request }: { request: ITripPlanRequest }) {
	if (!request.trip_source) {
		return null
	}
	if (request.trip_source.is_agent) return <Badge status="warning">B2B</Badge>
	return <Badge status="purple">B2C</Badge>
}

export function SelectLeadPurposes({
	linkToExisting,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	linkToExisting?: boolean
}) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			cacheKey={`lead-purpose${linkToExisting ? "link-to-exisitng" : ""}`}
			fetch={(q) =>
				LeadXHR(xhr)
					.getLeadPurposes({ q })
					.then((purposes) =>
						linkToExisting
							? purposes.filter((p) => p.config.link_to_existing_trip)
							: purposes
					)
			}
			{...props}
		/>
	)
}
